import React, { useState } from 'react';
import { I18nSupport } from '@/utils/types';
import { Button, Col, ComponentWithProps, Container, Row, Slider, useMedia } from '@visalex/core';

import cx from 'clsx';
import { useTranslation } from 'next-i18next';

import styles from './VisalexFeatures.module.scss';

export type VisalexFeaturesProps = I18nSupport & {
  featureIds: number[];
  buttonClass?: string;
  revertedSlider?: boolean;
};

const VisalexFeatures: ComponentWithProps<VisalexFeaturesProps> = ({
  i18nPrefix,
  featureIds,
  buttonClass,
  revertedSlider,
  className,
  ...rest
}) => {
  const { t } = useTranslation();
  const [activePage, setActivePage] = useState(0);
  const isMobile = useMedia<boolean>(['(max-width: 768px)'], [true], false);
  const learnMore = t(`${i18nPrefix}.learn-more`);

  return (
    <section
      {...rest}
      className={cx(className, styles['visalex-features'], revertedSlider && styles['visalex-features_reverted'])}
    >
      <Container className={cx(styles['visalex-features__container'], 'full-width-mobile')}>
        <Row className={styles['visalex-features__heading']}>
          <Col xs={12} xl={7} className="mx-auto text-center">
            <h3 className="text-lg-center">{t(`${i18nPrefix}.what-we-offer`)}</h3>
            {learnMore && <p>{learnMore}</p>}
          </Col>
        </Row>
        <Row className={cx('d-none d-xl-flex', styles['visalex-features__tabs'])}>
          {featureIds.map((id) => (
            <Col key={id} className={buttonClass ? buttonClass : styles['visalex-features__tab']}>
              <Button
                theme="default"
                onClick={() => setActivePage(id)}
                className={cx(styles['visalex-features__tab-content'], {
                  [styles['visalex-features__tab-content--active']]: id === activePage,
                })}
              >
                {t(`${i18nPrefix}.features.${id}.title`)}
              </Button>
            </Col>
          ))}
        </Row>
        <Row>
          <Col xl={12} className="mx-auto">
            <Slider
              activePage={activePage}
              setActivePage={setActivePage}
              showArrows={!isMobile}
              showDots
              pageRotationThreshold={isMobile ? 1 / 4 : 1 / 3}
            >
              {featureIds.map((id) => (
                <div key={id} className={styles['visalex-features__feature']}>
                  <Col xs={12} xl={6} className={styles['visalex-features__feature-illustration-holder']}>
                    <img
                      className={styles['visalex-features__feature-illustration']}
                      src={t(`${i18nPrefix}.features.${id}.imageUrl`)}
                      alt={t(`${i18nPrefix}.features.${id}.title`)}
                    />
                  </Col>
                  <Col xs={12} xl={6} className={styles['visalex-features__feature-content']}>
                    <h5>{t(`${i18nPrefix}.features.${id}.title`)}</h5>
                    <p>{t(`${i18nPrefix}.features.${id}.description`)}</p>
                  </Col>
                </div>
              ))}
            </Slider>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default VisalexFeatures;
